import axiosInstance from 'api/axios';
import { type AxiosError } from 'axios';
import { StatusEnum, statusOrder } from 'enums/StatusEnum';
import { type ActivityLogs } from 'interfaces/activityLog/ActivityLog.interface';
import {
  type AnomalyData,
  type HeatMapDataResponse,
  type ListAnomalies,
  type ListAnomaliesDashboard,
  type SearchAssignableUsers,
  type TimeSeries,
} from 'interfaces/change/ChangeData.interface';
import queryClient from 'lib/queryClient';
import { type UtilityTypeFiltersResponse } from 'interfaces/assets/Meters.interface';
import {
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { handleError } from 'utils/handleError/handleError';
import { handleSuccess } from 'utils/handleSuccess/handleSuccess';
import { type SubOptionsResponse } from 'interfaces/request/SubOptionsResponse.interface';

/**
 * Fetch anomaly data by ID using a React Query hook.
 *
 * This function makes an HTTP request to retrieve anomaly data based on the provided ID.
 *
 * @param {string} [id] - The ID of the anomaly data to fetch.
 *
 * @returns {UseQueryResult<AnomalyData | null, Error | AxiosError>} The result of the React Query hook, including the anomaly data or an error if one occurs.
 */

interface ChangeListPayload {
  page: number;
  per_page: number;
  query?: string;
  utility_type?: string;
  status?: string;
  group_id?: string;
  asset_id?: string;
  order_by?: string;
  show_only_assigned?: boolean;
}

export const useFetchChange = (id?: string): UseQueryResult<AnomalyData> => {
  return useQuery<AnomalyData, AxiosError>({
    queryKey: ['anomaly', id],
    queryFn: async () => {
      if (!id) {
        return null;
      }
      const response = await axiosInstance.get(`anomaly_detection/${id}`);
      return response.data;
    },
    retry: 2,
  });
};

interface AssignableUsersParams {
  change_id: string;
  query_str?: string;
}

export const useFetchAssignableUsers = (
  queryStr?: string,
  changeId?: string
): UseQueryResult<SearchAssignableUsers> => {
  const normalizedQuery = queryStr?.trim() ?? '';

  return useQuery<SearchAssignableUsers, AxiosError>({
    queryKey: ['assignableUsers', changeId, normalizedQuery],
    queryFn: async (): Promise<SearchAssignableUsers> => {
      if (!changeId) {
        throw new Error('Change ID is required to fetch assignable users');
      }

      const params: AssignableUsersParams = { change_id: changeId };

      if (normalizedQuery) {
        params.query_str = normalizedQuery;
      }

      const response = await axiosInstance.get<SearchAssignableUsers>(
        'anomaly_detection/get_assignable_users',
        { params }
      );

      return response.data;
    },
    enabled: !!changeId,
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes - users don't change frequently
  });
};

export const useFetchTimeSeries = ({
  id,
  momentOfDetectedAnomaly,
  disabled = false,
}: {
  id?: string;
  momentOfDetectedAnomaly?: string;
  disabled?: boolean;
}): UseQueryResult<TimeSeries | null, Error | AxiosError> => {
  return useQuery({
    queryKey: ['timeseries', id, momentOfDetectedAnomaly],
    queryFn: async () => {
      if (!id || !momentOfDetectedAnomaly || disabled) {
        return null;
      }
      const response = await axiosInstance.get<AnomalyData>(
        `anomaly_detection/ts`,
        {
          params: {
            group_by: 'hour',
            connection_point_id_client: id,
            moment_change: momentOfDetectedAnomaly,
          },
        }
      );
      return response.data;
    },
  });
};

export const useUpdateStatus = (
  id?: string
): UseMutationResult<any | null, Error | AxiosError> => {
  return useMutation({
    mutationFn: async (body) => {
      if (!id) {
        return null;
      }
      await axiosInstance.patch(`anomaly_detection/${id}/status`, body);
    },
    onError: (error: Error | AxiosError) => {
      handleError(error);
    },
    onSuccess: async () => {
      toast.success('Status updated');
      await queryClient.invalidateQueries({
        queryKey: ['anomaly'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['activityLog'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['listChanges'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['listChangesStatus'],
      });
    },
  });
};

export const useUpdateAssignment = (
  id?: string
): UseMutationResult<any | null, Error | AxiosError> => {
  return useMutation({
    mutationFn: async (body) => {
      if (!id) {
        return null;
      }
      await axiosInstance.patch(`anomaly_detection/${id}/assign`, body);
    },
    onError: (error: Error | AxiosError) => {
      handleError(error);
    },
    onSuccess: async () => {
      toast.success('Responsible updated');
      await queryClient.invalidateQueries({
        queryKey: ['activityLog'],
      });
    },
  });
};

export const useUpdateChange = (
  id?: string
): UseMutationResult<any | null, Error | AxiosError> => {
  return useMutation({
    mutationFn: async (body) => {
      if (!id) {
        return null;
      }
      await axiosInstance.patch(`anomaly_detection/${id}`, body);
    },
    onError: (error: Error | AxiosError) => {
      handleError(error);
    },
    onSuccess: async () => {
      handleSuccess('Change updated successfully');
      void queryClient.invalidateQueries({ queryKey: ['activityLog'] });
    },
  });
};

export const useUpdateRootCause = (
  changeId?: string
): UseMutationResult<
  any | null,
  Error | AxiosError,
  { root_cause_id: number | null }
> => {
  return useMutation({
    mutationFn: async (body: { root_cause_id: number | null }) => {
      if (!changeId) {
        return null;
      }
      const response = await axiosInstance.patch(
        `anomaly_detection/${changeId}/root_cause`,
        body
      );
      return response.data;
    },
    onError: (error: Error | AxiosError) => {
      handleError(error);
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ['enervisionRootCauseList'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['currentRootCause'],
      });
      handleSuccess('Root cause updated successfully');
      void queryClient.invalidateQueries({ queryKey: ['activityLog'] });
    },
  });
};

export const useAnomalieDetectionList = ({
  page = 1,
  perPage = 20,
  query = '',
  utilityType = '',
  status = '',
  group,
  asset,
  orderBy = '-moment_change',
  showOnlyAssigned = false,
}: {
  page?: number;
  perPage?: number;
  query?: string;
  utilityType?: string;
  status?: string;
  group?: number;
  asset?: number;
  orderBy?: string;
  showOnlyAssigned?: boolean;
} = {}): UseInfiniteQueryResult<InfiniteData<ListAnomalies>> => {
  return useInfiniteQuery<ListAnomalies>({
    queryKey: [
      'listChanges',
      query,
      utilityType,
      status,
      group,
      asset,
      orderBy,
      showOnlyAssigned,
    ],
    queryFn: async ({ pageParam }) => {
      const params: ChangeListPayload = {
        page: pageParam as number,
        per_page: perPage,
        query,
        utility_type: utilityType,
        status,
        group_id: group?.toString(),
        asset_id: asset?.toString(),
        order_by: orderBy,
        show_only_assigned: showOnlyAssigned,
      };

      if (params.utility_type === '') {
        delete params.utility_type;
      }

      if (!params.group_id) {
        delete params.group_id;
      }

      if (!params.asset_id) {
        delete params.asset_id;
      }

      if (params.status === '') {
        delete params.status;
      }

      if (params.show_only_assigned === false) {
        delete params.show_only_assigned;
      }

      const response = await axiosInstance.get(`anomaly_detection/filter`, {
        params,
      });
      return response.data;
    },
    initialPageParam: page,
    getNextPageParam: (lastPage) => {
      if (lastPage?.hasNextPage) {
        return lastPage.nextPage;
      }
    },
    refetchOnWindowFocus: false,
  });
};

export const useListDropdownChanges = ({
  page = 1,
  perPage = 20,
  connectionPointIdClient,
}: {
  page?: number;
  perPage?: number;
  connectionPointIdClient?: string;
} = {}): UseQueryResult<ListAnomalies> => {
  return useQuery<ListAnomalies>({
    queryKey: ['dropdownListChanges', connectionPointIdClient],
    queryFn: async () => {
      if (!connectionPointIdClient) return;
      const response = await axiosInstance.get(
        `anomaly_detection/list/anomalies`,
        {
          params: {
            connection_point_id_client: connectionPointIdClient,
          },
        }
      );
      return response.data;
    },
  });
};

export const useFetchAnomalyTableStatus = ({
  query,
  groupQuery,
  utilityType,
  assetQuery,
  showOnlyUserChanges,
}: {
  query?: string;
  groupQuery?: number;
  utilityType?: string;
  assetQuery?: number;
  showOnlyUserChanges?: boolean;
}): UseQueryResult<any | null, Error | AxiosError> => {
  return useQuery({
    queryKey: [
      'listChangesStatus',
      query,
      groupQuery,
      utilityType,
      assetQuery,
      showOnlyUserChanges,
    ],
    queryFn: async () => {
      const params = Object.fromEntries(
        Object.entries({
          query,
          group_id: groupQuery,
          utility_type: utilityType,
          asset_id: assetQuery,
          show_only_assigned: showOnlyUserChanges,
        }).filter(([_, value]) => value) // Filters out any falsy values
      );
      const response = await axiosInstance.get('anomaly_detection/status', {
        params,
      });
      const statusData: Record<string, number> = response.data;
      const sortedStatus: Record<string, number> = {};

      statusOrder.forEach((key) => {
        const value = StatusEnum[key as keyof typeof StatusEnum];
        if (value && statusData) {
          sortedStatus[value] = statusData[value];
        }
      });

      return sortedStatus;
    },
  });
};

export const useFetchRootCauses = (): UseQueryResult<SubOptionsResponse[]> => {
  return useQuery<SubOptionsResponse[]>({
    queryKey: ['rootCauses'],
    queryFn: async () => {
      const response = await axiosInstance.get('anomaly_detection/root_causes');
      return response.data;
    },
  });
};

export const useFetchActivityLog = (
  changeDetectionId?: number
): UseQueryResult<ActivityLogs> => {
  return useQuery<ActivityLogs>({
    queryKey: ['activityLog', changeDetectionId],
    queryFn: async () => {
      if (!changeDetectionId) return;
      const response = await axiosInstance.get(
        `anomaly_detection/${changeDetectionId}/activity`
      );
      return response.data;
    },
  });
};

export const useFetchHeatMap = (
  id?: string,
  momentOfDetectedAnomaly?: string
): UseQueryResult<HeatMapDataResponse> => {
  return useQuery<HeatMapDataResponse>({
    queryKey: ['heatMap', id, momentOfDetectedAnomaly],
    queryFn: async () => {
      if (!id || !momentOfDetectedAnomaly) {
        return null;
      }
      const response = await axiosInstance.get<any>(
        `anomaly_detection/heatmap/${id}/${momentOfDetectedAnomaly}`
      );
      return response.data;
    },
  });
};

export const usePostActivityLogComment = (
  changeId?: number
): UseMutationResult<any | null, Error | AxiosError> => {
  return useMutation({
    mutationFn: async (body) => {
      if (!changeId) return;
      const response = await axiosInstance.post(
        `anomaly_detection/${changeId}/activity`,
        body
      );
      return response?.data;
    },
    onError: (error: Error | AxiosError) => {
      handleError(error);
    },
    onSuccess: async () => {
      toast.success('Added a new comment');
      await queryClient.invalidateQueries({ queryKey: ['activityLog'] });
    },
  });
};

export const useFetchUtilityTypeFilters = (): UseQueryResult<
  UtilityTypeFiltersResponse | undefined,
  Error | AxiosError
> => {
  return useQuery({
    queryKey: ['utilityTypeFilters'],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `anomaly_detection/utility_type_filters`
      );
      return response.data;
    },
  });
};

export const useFetchBuildingTopChanges = (
  id?: string
): UseQueryResult<ListAnomaliesDashboard | undefined, Error | AxiosError> => {
  return useQuery({
    queryKey: ['fetchBuildingTopChanges', id],
    queryFn: async () => {
      if (!id) return;
      const response = await axiosInstance.get(
        `anomaly_detection/building/score/${id}`
      );
      return response.data;
    },
  });
};
